// Single source of truth for QuotationEditor hotkeys.
//
// Every shortcut in the editor (and, in follow-up REQ-360 tasks, the right sidebar and the
// remaining actions) is declared here. The hotkey help panel (REQ-359) renders directly from
// EDITOR_HOTKEYS, and useQuotationViewer binds them through createEditorHotkeys + tinykeys.
//
// Combos are tinykeys strings. Where a binding must survive keyboard layouts and the macOS
// Option-key producing a different printable character (⌥1 → "¡"), the combo uses the physical
// `event.code` form (e.g. "Alt+Digit1") rather than the printable `event.key`.
//
// NOTE on the modifier: REQ-360 specs mod+1 (Ctrl on Win/Linux, Cmd on Mac), but Ctrl/Cmd+1 is
// reserved by Chrome/Edge/Firefox for tab-switching and cannot be intercepted by a web page.
// We ship Alt+1 (≡ Option+1 on macOS) as the interceptable default. The collision is escalated
// to design; the follow-up right-sidebar task must mirror this as Alt+3. Combos are declared
// here precisely so a design-approved remap is a one-line change.

export type THotkeyScope = "editor" | "leftSidebar" | "rightSidebar"

export interface IHotkeyMeta {
	// Stable identifier — used to attach handlers and (later) to look up help-panel copy.
	id: string
	// tinykeys combo string, e.g. "Alt+Digit1", "Tab", "Shift+Tab", "Enter".
	combo: string
	// Human-readable combo for the help panel, e.g. "Alt + 1".
	label: string
	// What the shortcut does, for the help panel.
	description: string
	// Which context the shortcut belongs to.
	scope: THotkeyScope
}

export interface IHotkey extends IHotkeyMeta {
	handler: (event: KeyboardEvent) => void
}

export const HOTKEY_IDS = {
	focusLeftSidebar: "focus-left-sidebar",
	cycleFocusForward: "cycle-focus-forward",
	cycleFocusBackward: "cycle-focus-backward",
	activateItem: "activate-item",
	navigateNextEref: "navigate-next-eref",
	navigatePrevEref: "navigate-prev-eref",
	navigateFurnitureToEref: "navigate-furniture-to-eref",
	focusRightSidebar: "focus-right-sidebar",
	closeRightSidebar: "close-right-sidebar",
} as const

export const EDITOR_HOTKEYS: IHotkeyMeta[] = [
	{
		id: HOTKEY_IDS.focusLeftSidebar,
		combo: "Alt+Digit1",
		label: "Alt + 1",
		description: "Focus the elevation references sidebar",
		scope: "editor",
	},
	{
		id: HOTKEY_IDS.cycleFocusForward,
		combo: "Tab",
		label: "Tab",
		description: "Move focus to the next item in the sidebar",
		scope: "leftSidebar",
	},
	{
		id: HOTKEY_IDS.cycleFocusBackward,
		combo: "Shift+Tab",
		label: "Shift + Tab",
		description: "Move focus to the previous item in the sidebar",
		scope: "leftSidebar",
	},
	{
		id: HOTKEY_IDS.activateItem,
		combo: "Enter",
		label: "Enter",
		description: "Open the focused reference or object",
		scope: "leftSidebar",
	},
	// Right-sidebar navigation (REQ-360, tasks 2 & 3). The spec is mod+Arrow (Ctrl on Win/Linux,
	// Cmd on Mac); we ship Alt (≡ Option on macOS) to match the interceptable Alt+1 above. Arrow
	// keys aren't printable, so event.key stays "ArrowDown/Up/Left" under Alt on every layout — no
	// event.code form is needed here. Alt+ArrowLeft/Right is the browser Back/Forward shortcut, so
	// the handler always preventDefaults these while the editor owns them (see useEditorHotkeys).
	{
		id: HOTKEY_IDS.navigateNextEref,
		combo: "Alt+ArrowDown",
		label: "Alt + ↓",
		description: "Open the next elevation reference",
		scope: "rightSidebar",
	},
	{
		id: HOTKEY_IDS.navigatePrevEref,
		combo: "Alt+ArrowUp",
		label: "Alt + ↑",
		description: "Open the previous elevation reference",
		scope: "rightSidebar",
	},
	{
		id: HOTKEY_IDS.navigateFurnitureToEref,
		combo: "Alt+ArrowLeft",
		label: "Alt + ←",
		description: "Open the elevation reference connected to this object",
		scope: "rightSidebar",
	},
	// Right-sidebar focus + close (REQ-359/REQ-360, this task). Alt+3 mirrors the left sidebar's
	// Alt+1 (spec is mod+3 / Cmd+3, shipped as the interceptable Alt+3 — Ctrl/Cmd+3 is reserved by
	// the browser for tab-switching; Alt+2 is left for the middle nav menu). Uses the physical
	// event.code form so macOS Option+3 (→ "£") still matches. Escape closes the right sidebar and
	// returns focus to the left sidebar; it is exempt from the ignore-while-typing rule so it works
	// from inside a sidebar text field (the field handles its own Escape first — see useEditorHotkeys).
	{
		id: HOTKEY_IDS.focusRightSidebar,
		combo: "Alt+Digit3",
		label: "Alt + 3",
		description: "Focus the reference details sidebar",
		scope: "rightSidebar",
	},
	{
		id: HOTKEY_IDS.closeRightSidebar,
		combo: "Escape",
		label: "Esc",
		description: "Close the reference details sidebar",
		scope: "rightSidebar",
	},
]

export type THotkeyHandlers = Record<string, (event: KeyboardEvent) => void>

// Attach handlers (keyed by hotkey id) to the declared metadata. Entries without a handler
// are skipped, so callers can wire up a subset of scopes incrementally.
export const createEditorHotkeys = (handlers: THotkeyHandlers): IHotkey[] =>
	EDITOR_HOTKEYS.filter((meta) => typeof handlers[meta.id] === "function").map((meta) => ({
		...meta,
		handler: handlers[meta.id],
	}))
